import cn from "classnames"
import React from "react"

import { AudioMark } from "../AudioMark"

import style from "./style.module.scss"

import { Img } from "ui-kit/components-branded/Img"
import { formatExperience, formatTitlesShort } from "utils/helpers/psychologistUtil"
import { PsychologistAvatar } from "components/PsychologistAvatar/PsychologistAvatar"
import { useSearchParams } from "utils/customHooks"
import { numberToYearsInRussian } from "utils/infrastructure/wording-utils"
import { Psychologist } from "utils/interfaces"
import { getLocationPath } from "utils/helpers"
import { FriendlyLabel } from "components/Labels"
import { APP_STATUSES } from "utils/AppStatusContext"
import { ProfileLink } from "app/AppRouter/links"
import { useMaxExperience } from "utils/hooks"

export type Classes = Partial<
    Record<
        "avatar" | "title" | "labels" | "header" | "mainInfo" | "additionalInfo" | "specialization",
        string
    >
>

interface HeaderContentProps {
    psy: Psychologist
    freeBooking?: boolean
    showAvailability?: boolean
    classes?: Classes
}

export const HeaderContent = ({ psy, showAvailability = true, classes }: HeaderContentProps) => {
    const { experience } = useMaxExperience(psy)

    return (
        <header className={cn(style.container, classes?.header)}>
            <div className={classes?.avatar}>
                <PsychologistAvatar
                    psy={psy}
                    size="L"
                    renderAfter={psy.audio || psy.video ? <AudioMark /> : null}
                />
            </div>
            <div className={cn(style.info, classes?.mainInfo)}>
                <div className={cn(style.info__name, classes?.title)}>{psy.display_name}</div>

                <div className={cn(style.info__title, classes?.specialization)}>
                    {formatTitlesShort(psy.titles)}
                </div>

                <div className={cn(style.additional, classes?.additionalInfo)}>
                    <div>Опыт {formatExperience(experience)}</div>
                    {showAvailability && psy.age && (
                        <>
                            <div>•</div>
                            {psy.age}&nbsp;{numberToYearsInRussian(psy.age)}
                        </>
                    )}
                </div>
            </div>
            <div className={cn(style.labels, classes?.labels)}>
                <FriendlyLabel psy={psy} />
                {psy.working_with?.adults.foreigner && (
                    <div className="labels__label mb-2 mr-2">
                        <Img src="globe-emoji.png" className="labels__emoji" />
                        Сессии на английском
                    </div>
                )}
            </div>
        </header>
    )
}

interface HeaderProps {
    psy: Psychologist
    index?: string
    target?: string
    from?: APP_STATUSES
    classes?: Classes
    showAvailability?: boolean
    showPsysStory?: number
    onClick?: () => void
}

export const Header = ({
    psy,
    index,
    target,
    from,
    showAvailability,
    classes,
    showPsysStory,
    onClick,
}: HeaderProps) => {
    const searchParams = useSearchParams()
    const resultsKey = searchParams.get("results_key") as string
    const pathName = getLocationPath()

    return (
        <ProfileLink
            profileUrl={psy.profile_url}
            position={index}
            className={style.profile}
            target={target}
            from={from}
            fromLink={pathName}
            resultsKey={resultsKey}
            showPsysStory={showPsysStory}
            onClick={onClick}
        >
            <HeaderContent
                {...{
                    psy,
                    index,
                    target,
                    from,
                    showAvailability,
                    classes,
                }}
            />
        </ProfileLink>
    )
}
